import React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import styles from "./CustomButton.module.css";

const CustomButton = ({
  children,
  type = "button",
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  loadingText = "Loading...",
  onClick,
  className = "",
  fullWidth = true,
  icon,
  iconPosition = "left",
  rounded = false,
  ...rest
}) => {
  const sizeMap = {
    sm: "small",
    md: "medium",
    lg: "large",
  };

  // Map Bootstrap variants to MUI color prop
  const getColorAndVariant = () => {
    const isOutline = variant.startsWith("outline-");
    const baseVariant = isOutline ? variant.replace("outline-", "") : variant;

    const colorMap = {
      primary: "primary",
      secondary: "secondary",
      success: "success",
      danger: "error",
      warning: "warning",
      info: "info",
      light: "inherit",
      dark: "inherit",
    };

    return {
      color: colorMap[baseVariant] || "primary",
      muiVariant: variant,
    };
  };

  const { color, muiVariant } = getColorAndVariant();

  const getCustomStyles = () => {
    const baseStyles = {
      borderRadius: rounded ? "50px" : "6px",
      textTransform: "none",
      fontWeight: 500,
      boxShadow:
        muiVariant === "contained" ? "0 2px 4px rgba(0, 0, 0, 0.1)" : "none",
      "&:hover": {
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      },
      "&:active": {
        transform: "translateY(0)",
      },
    };

    // Custom colors for light and dark variants
    if (variant === "light" || variant === "outline-light") {
      return {
        ...baseStyles,
        backgroundColor: variant === "light" ? "#f8f9fa" : "transparent",
        color: "#212529",
        border: variant === "outline-light" ? "1px solid #f8f9fa" : "none",
        "&:hover": {
          ...baseStyles["&:hover"],
          backgroundColor: "#e2e6ea",
        },
      };
    }

    if (variant === "dark" || variant === "outline-dark") {
      return {
        ...baseStyles,
        backgroundColor: variant === "dark" ? "#212529" : "transparent",
        color: variant === "dark" ? "#fff" : "#212529",
        border: variant === "outline-dark" ? "1px solid #212529" : "none",
        "&:hover": {
          ...baseStyles["&:hover"],
          backgroundColor:
            variant === "dark" ? "#1a1e21" : "rgba(33, 37, 41, 0.1)",
        },
      };
    }

    return baseStyles;
  };

  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      variant={muiVariant}
      color={color}
      size={sizeMap[size]}
      fullWidth={fullWidth}
      className={`${styles.button} ${className} ${
        loading ? styles.loading : ""
      }`}
      sx={getCustomStyles()}
      {...rest}
    >
      {loading ? (
        <>
          <CircularProgress
            size={size === "sm" ? 16 : size === "lg" ? 24 : 20}
            color="inherit"
            className={styles.spinner}
          />
          <span className={styles.loadingText}>{loadingText}</span>
        </>
      ) : (
        <>
          {icon && iconPosition === "left" && (
            <span className={styles.iconLeft}>{icon}</span>
          )}
          {children}
          {icon && iconPosition === "right" && (
            <span className={styles.iconRight}>{icon}</span>
          )}
        </>
      )}
    </Button>
  );
};

export default CustomButton;
